import React, {useRef} from "react";
import styled from "styled-components";
import {TitleLabel, SectionTitle} from "../components";
import useModalService from "../../../common/hooks/useModalService";
import CartModalService from "./cartModalService";
import CartErrors from "./CartErrors";
import {ScrollableSection} from "../components";
import Loader from "../../../common/components/elements/Loader";
import useCartState from "src/core/checkout/hooks/useCartState";
import {CloseIcon} from "../../../common/components/elements/button/CloseButton";
import {Trash2} from "styled-icons/feather/Trash2";
import useFeatureToggles from "../../../common/hooks/useFeatureToggles";
import useClickOutside from "src/core/common/hooks/useClickOutside";
import AllowedToCheckout from "../AllowedToCheckout";
import CartItems from "./CartItems";
import DisableIfLoading from "../DisableIfLoading";
import CartTotals from "./CartTotals";
import {RequiresOnBoarding} from "../../../authentication/onboarding/CheckoutOnBoarding";
import routes from "../../../common/routes";
import Separator from "../../../common/components/elements/Separator";
import {Section} from "../../../common/components/elements/container/section";
import FilledButton from "../../../common/components/elements/button/FilledButton";
import LoyaltyHeader from "src/core/user/components/LoyaltyHeader";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import Title from "src/core/common/components/modules/Title";
import ProductRecommendations from "./ProductRecommendations";
import useSite from "src/core/sites/hooks/useSite";
import {isMobile} from "src/core/common/utils";

function Cart({closeCart, openCart, isOpen}) {
  const [, authApi] = useAuthentication();
  const [cartState, actions] = useCartState();
  const site = useSite();
  const options = site.getOptions();
  const toggles = useFeatureToggles();

  const wrapperRef = useRef(null);

  useClickOutside(wrapperRef, evt => {
    const ignoreClasses = ["Toastify", "Dropdown-option"];
    const classNames = evt.target.className.toString();
    const ignore = ignoreClasses.find(className => classNames.indexOf(className) > -1);
    if (!evt.target.getAttribute("data-keep-cart") && !ignore) {
      closeCart();
    }
  });

  useModalService(CartModalService, openCart);
  const cart = cartState.data;
  const hasItems = cart ? cart.hasItems() : false;
  const productsInCart = cart && cart.getItems().map(item => item.getProduct());

  const showLoyalty = Boolean(authApi.userProfile());

  // TODO: refactor integrations
  return (
    <CartContainer
      isOpen={isOpen}
      ref={wrapperRef}
      forceShow={isMobile() && options.qrCodeOnboardingEnabled()}
    >
      <div>
        <CustomTitle textAlign={"left"}>Your Cart</CustomTitle>
      </div>
      <CloseButton onClick={closeCart} />
      <CartErrors />
      <ScrollableSection style={{overflowX: "hidden"}}>
        <DisableIfLoading>
          <CartItems
            footer={
              toggles.showRecommendedProductsInCart() && (
                <ProductRecommendations productsInCart={productsInCart} />
              )
            }
          />
        </DisableIfLoading>
      </ScrollableSection>
      <Separator />
      {showLoyalty && <CustomLoyaltyHeader showPoints={false} mode={"small"} />}
      <Separator />
      <Section>
        {hasItems ? (
          <>
            {cartState.loading && <CustomLoader />}
            <SectionTitle>
              <TitleLabel>Cart Totals</TitleLabel>
              {!cartState.loading && toggles.showEmptyCartButton() && (
                <EmptyCart onClick={() => actions.clearCart()}>
                  <TrashIcon />
                  Empty cart
                </EmptyCart>
              )}
            </SectionTitle>
          </>
        ) : (
          <NoProductsLoaderContainer loading={cartState.loading.toString()}>
            {cartState.loading && <CustomLoader />}
          </NoProductsLoaderContainer>
        )}
        <AllowedToCheckout>
          {({allowed}) => (
            <>
              <CartTotals />
              <RequiresOnBoarding
                onSuccessGoTo={routes.checkout}
                render={({start}) => (
                  <FilledButton
                    dataCy="startCheckoutButton"
                    disabled={!allowed}
                    onClick={() => {
                      closeCart();
                      start();
                    }}
                    label="Checkout"
                  />
                )}
              />
            </>
          )}
        </AllowedToCheckout>
      </Section>
    </CartContainer>
  );
}

let CustomTitle = styled(Title)`
  text-align: left;
  width: fit-content;
  font-weight: ${({theme}) => theme.v2.typography.titles.weight.heavy};
  color: ${({theme}) =>
    theme.v2.overrides.cart?.color?.onBackground || theme.v2.color.onBackground};
  background-color: ${({theme}) =>
    theme.v2.overrides?.cart?.color?.background || theme.v2.color.background};
  padding: ${({theme}) =>
    theme.v2.overrides?.cart?.color?.background ? "6px 10px" : "0px"};
`;

const CustomLoyaltyHeader = styled(LoyaltyHeader)`
  margin-bottom: ${({theme}) => theme.v2.spacing(2)};
`;

const CustomLoader = styled(Loader)`
  position: absolute;
  right: ${({theme}) => theme.v2.spacing(4)};
  top: ${({theme}) => theme.v2.spacing(4)};
`;
const CartContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  right: 0;
  top: 0;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  box-sizing: border-box;
  width: 428px;
  background-color: white;
  z-index: 9;
  box-shadow: ${props => (props.isOpen ? "0 2px 18px 0 rgba(0, 0, 0, 0.3)" : "none")};
  padding: ${({theme}) => theme.v2.spacing(4)};
  transform: translate3d(${({isOpen}) => (isOpen ? "0" : "100%")}, 0, 0);
  transition: transform 300ms ease-out;

  > h2 {
    margin-bottom: ${({theme}) => theme.v2.spacing(6)};
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 428px) {
    max-width: 100vw;
  }

  @media (max-width: 767px) {
    z-index: ${({forceShow}) => forceShow && 999};
  }
`;
const CloseButton = styled(CloseIcon)`
  position: absolute;
  right: ${({theme}) => theme.v2.spacing(4)};
  top: ${({theme}) => theme.v2.spacing(4)};
  cursor: pointer;
`;
const EmptyCart = styled.div`
  display: flex;
  align-items: center;

  font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
  font-weight: ${({theme}) => theme.v2.typography.bodyText1.weight.light};
  color: gray;
  margin: 0;
  cursor: pointer;
`;
const TrashIcon = styled(Trash2)`
  color: gray;
  margin-right: 4px;
  height: ${({theme}) => theme.v2.typography.sizing.m.lg};
  width: ${({theme}) => theme.v2.typography.sizing.m.lg};
`;
const NoProductsLoaderContainer = styled.div`
  height: ${({loading}) => (loading === "true" ? "20px" : "0px")};
`;

export default Cart;
